import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getCarrito, addCarritoItem, updateCarritoItem, removeCarritoItem } from '../api/carrito';

// Estado global del carrito, para que el badge del nav (src/App.js) y la
// página /carrito (src/pages/Carrito.jsx) compartan los mismos datos sin
// prop drilling.
const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(
    () =>
      getCarrito()
        .then((data) => {
          setItems(data.items || []);
          setTotal(data.total || 0);
        })
        .catch(() => {
          // Si falla, el carrito queda como estaba -- un error de red acá
          // no debería bloquear el resto de la navegación del sitio.
        })
        .finally(() => setLoading(false)),
    [],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addItem = useCallback((payload) => addCarritoItem(payload).then(() => refresh()), [refresh]);
  const updateItem = useCallback((itemId, cantidad) => updateCarritoItem(itemId, cantidad).then(() => refresh()), [refresh]);
  const removeItem = useCallback((itemId) => removeCarritoItem(itemId).then(() => refresh()), [refresh]);

  // Cada item del carrito es 1 pack de estampitas (la cantidad es cuántas
  // estampitas trae ese pack -- 24, 36, 48... -- no cuántos packs hay), así
  // que el badge del nav cuenta packs (items), no la suma de `cantidad`.
  const itemCount = items.length;

  const value = { items, total, itemCount, loading, refresh, addItem, updateItem, removeItem };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

CartProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe usarse dentro de un CartProvider');
  }
  return context;
};
